import { parsePlatform } from '@/lib/utils/parse-platform';
import type {
  ClientTelemetry,
  ClientTelemetryAtUpload
} from '@/lib/services/recordings';

/**
 * AudioFormat → MediaRecorder mime string. Mirrors `detectMimeType()` in
 * `use-media-recorder.ts` — if that helper grows new branches, this needs
 * to grow with it.
 */
export function recorderMimeFromFormat(
  format: 'webm' | 'mp4'
): 'audio/webm;codecs=opus' | 'audio/mp4' {
  return format === 'mp4' ? 'audio/mp4' : 'audio/webm;codecs=opus';
}

/**
 * Mime → MediaRecorder start strategy. Mirrors the mime-conditional branch
 * in `use-media-recorder.ts`. See
 * `.claude/tasks/20260522-ios-mp4-chunking-fix/`.
 */
export function chunkingStrategyFromMime(
  mime: string
): ClientTelemetry['chunkingStrategy'] {
  if (mime === 'audio/mp4') return 'timeslice-1000';
  if (mime.startsWith('audio/webm')) return 'manual-request-data-1000';
  return 'unknown';
}

/**
 * The platform block written into `clientTelemetry` — structured `os` /
 * `osVersion` / `browser` for every device (VALE-212), plus the legacy
 * `iosVersion` back-fill.
 *
 * `iosVersion` predates the structured fields and is kept populated on
 * purpose: historical JSONB rows already carry it, so leaving it in means
 * support can read old and new recordings the same way. It is a pure
 * pass-through — nothing on either side reads it for logic — so the cost is
 * one duplicated string.
 *
 * The back-fill covers `iPadOS` as well as `iOS` because `parsePlatform`
 * reports an iPad as `iPadOS`, and `iOS` alone would leave every iPad's
 * `iosVersion` blank. That is a deliberate (small) widening: the
 * `parseIosVersion` this replaced matched only the UA's `iPhone OS` token, and
 * an iPad spells the same thing `CPU OS`, so iPads were blank there too — the
 * same blind spot as Android, just narrower.
 */
function platformTelemetry(
  userAgent: string
): Pick<ClientTelemetry, 'os' | 'osVersion' | 'browser' | 'iosVersion'> {
  const { os, osVersion, browser } = parsePlatform(userAgent);
  return {
    os,
    osVersion,
    browser,
    iosVersion: os === 'iOS' || os === 'iPadOS' ? osVersion : undefined
  };
}

interface NavigatorConnectionLike {
  effectiveType?: string;
}

function getConnectionType(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const conn = (
    navigator as unknown as { connection?: NavigatorConnectionLike }
  ).connection;
  return conn?.effectiveType;
}

/**
 * Capture the telemetry known the moment the recorder is about to start.
 * Pure, synchronous. Reads `navigator`, the live audio track, and the
 * resolved audio format. Lawyer-invisible — payload is internal-only.
 *
 * Field rationale and BE contract:
 * `.claude/tasks/20260522-ios-mp4-chunking-fix/backend-brief.md`.
 */
export function gatherClientTelemetry(args: {
  audioFormat: 'webm' | 'mp4';
  stream: MediaStream;
}): ClientTelemetry {
  const { audioFormat, stream } = args;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const platform = typeof navigator !== 'undefined' ? navigator.platform : '';

  const recorderMimeType = recorderMimeFromFormat(audioFormat);
  const chunkingStrategy = chunkingStrategyFromMime(recorderMimeType);

  const track = stream.getAudioTracks()[0];
  let deviceId: string | undefined;
  try {
    deviceId = track?.getSettings().deviceId;
  } catch {
    deviceId = undefined;
  }

  return {
    userAgent: ua,
    platform,
    recorderMimeType,
    chunkingStrategy,
    recorderStartedAt: new Date().toISOString(),
    audioInputLabel: track?.label || undefined,
    audioInputDeviceId: deviceId || undefined,
    ...platformTelemetry(ua),
    // clientVersion intentionally absent — the FE bundle does not currently
    // inject a build ID. Tracked as an FE follow-up in backend-brief.md
    // "FE-side prerequisites" §4.
    clientVersion: undefined
  };
}

/**
 * Capture telemetry only knowable once the recorder has stopped and chunks
 * are assembled (Option C split in the backend brief).
 */
export function gatherClientTelemetryAtUpload(args: {
  chunkCount: number;
}): ClientTelemetryAtUpload {
  return {
    chunkCount: args.chunkCount,
    connectionType: getConnectionType()
  };
}

/**
 * LF-3743 mobile-handoff: telemetry captured at page-open time (before the
 * user taps Start). Used by the claim-on-mount path so the scan window
 * (2 min) flips to the session window (24 h) the moment the lawyer's client
 * lands on `/device-recording/[token]`.
 *
 * Stream-derived fields (`audioInputLabel`, `audioInputDeviceId`,
 * `sampleRate`) are absent here — they require `MediaStream` and only
 * become known at Start tap. The BE claim DTO is loosened to accept them
 * as optional and gets the recorder fields back via
 * `clientTelemetryAtUpload` on upload-complete (where they're naturally
 * available).
 */
export function gatherClientTelemetryAtOpen(args: {
  audioFormat: 'webm' | 'mp4';
}): ClientTelemetry {
  const { audioFormat } = args;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const platform = typeof navigator !== 'undefined' ? navigator.platform : '';

  const recorderMimeType = recorderMimeFromFormat(audioFormat);
  const chunkingStrategy = chunkingStrategyFromMime(recorderMimeType);

  return {
    userAgent: ua,
    platform,
    recorderMimeType,
    chunkingStrategy,
    recorderStartedAt: new Date().toISOString(),
    ...platformTelemetry(ua),
    // audioInputLabel / audioInputDeviceId / sampleRate intentionally absent
    // — those need a MediaStream which doesn't exist at page-open.
    // clientVersion intentionally absent — see `gatherClientTelemetry`.
    clientVersion: undefined
  };
}
